import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface DailyStats {
  date: string;
  totalShots: number;
  shotsByPlayer: Record<string, number>;
  averageSpeed: number;
}

interface Shot {
  player: string;
  speed: number;
  timestamp: any;
  recordedBy: string;
}

const Analytics: React.FC = () => {
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [totalRecordedShots, setTotalRecordedShots] = useState(0);
  const [averageSpeed, setAverageSpeed] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    fetchShotData();
  }, []);

  const fetchShotData = async () => {
    try {
      const shotsRef = collection(db, 'shots');
      const q = query(shotsRef, orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const shots = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as unknown as Shot[];

      // Calculate total shots and average speed
      const totalShots = shots.length; // Each document is now a unique shot
      const totalSpeed = shots.reduce((sum, shot) => sum + shot.speed, 0);
      setTotalRecordedShots(totalShots);
      setAverageSpeed(totalShots > 0 ? Math.round(totalSpeed / totalShots) : 0);

      // Process daily statistics
      const dailyData: Record<string, DailyStats> = {};
      
      shots.forEach(shot => {
        if (!shot.timestamp) return;
        
        const date = new Date(shot.timestamp.toDate()).toISOString().split('T')[0];
        if (!dailyData[date]) {
          dailyData[date] = {
            date,
            totalShots: 0,
            shotsByPlayer: {},
            averageSpeed: 0
          };
        }
        
        // Each shot is counted individually
        dailyData[date].totalShots++;
        dailyData[date].shotsByPlayer[shot.player] = (dailyData[date].shotsByPlayer[shot.player] || 0) + 1;
        dailyData[date].averageSpeed = (dailyData[date].averageSpeed * (dailyData[date].totalShots - 1) + shot.speed) / dailyData[date].totalShots;
      });

      const sortedDailyStats = Object.values(dailyData).sort((a, b) => b.date.localeCompare(a.date));
      setDailyStats(sortedDailyStats);
    } catch (error) {
      console.error('Error fetching shot data:', error);
    }
  };

  const chartData = {
    labels: dailyStats.map(day => new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
    datasets: [
      {
        label: 'Shots Recorded',
        data: dailyStats.map(day => day.totalShots),
        backgroundColor: '#f47b20',
        borderColor: '#ff8c33',
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: '#ffffff'
        }
      },
      title: {
        display: true,
        text: 'Daily Shot Recording Activity',
        color: '#ffffff',
        font: {
          size: 16
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: '#94a3b8'
        },
        grid: {
          color: '#1a2233'
        }
      },
      x: {
        ticks: {
          color: '#94a3b8'
        },
        grid: {
          color: '#1a2233'
        }
      }
    }
  };

  if (!currentUser) {
    return (
      <div className="max-w-md mx-auto bg-background-card rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-primary mb-4">Analytics</h2>
        <p className="text-text-secondary">Please log in to view analytics.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto bg-background-card rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-primary mb-6">Shot Recording Analytics</h2>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-background-navy p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-primary mb-2">Total Shots Recorded</h3>
          <p className="text-4xl font-bold text-primary">{totalRecordedShots}</p>
          <p className="text-text-secondary mt-2">All time recorded shots</p>
        </div>
        <div className="bg-background-navy p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-primary mb-2">Average Shot Speed</h3>
          <p className="text-4xl font-bold text-primary">{averageSpeed} KPH</p>
          <p className="text-text-secondary mt-2">Across all recorded shots</p>
        </div>
      </div>

      {/* Chart Container */}
      <div className="bg-background-navy p-4 rounded-lg mb-8" style={{ height: '400px' }}>
        <Bar data={chartData} options={chartOptions} />
      </div>

      {/* Daily Stats Table */}
      <div className="space-y-6">
        <h3 className="text-xl font-bold text-primary mb-4">Daily Recording History</h3>
        {dailyStats.map((day) => (
          <div key={day.date} className="bg-background-navy p-4 rounded-lg">
            <h4 className="text-lg font-bold text-primary mb-2">
              {new Date(day.date).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="text-text-secondary">Total Shots: </span>
                <span className="text-primary font-bold">{day.totalShots}</span>
              </div>
              <div>
                <span className="text-text-secondary">Average Speed: </span>
                <span className="text-primary font-bold">{Math.round(day.averageSpeed)} KPH</span>
              </div>
              <div>
                <span className="text-text-secondary">Recorded by: </span>
                <span className="text-primary font-bold">hishima</span>
              </div>
            </div>
            
            <div className="mt-4 space-y-2">
              <h5 className="text-sm font-semibold text-text-secondary">Shots by Player:</h5>
              {Object.entries(day.shotsByPlayer).map(([player, count]) => (
                <div key={player} className="flex justify-between items-center bg-background-card px-3 py-2 rounded">
                  <span className="text-text-primary">{player}</span>
                  <span className="text-primary font-bold">{count} shots</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        
        {dailyStats.length === 0 && (
          <p className="text-text-secondary text-center py-4">
            No shot data available yet.
          </p>
        )}
      </div>
    </div>
  );
};

export default Analytics; 